
import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function HireFooter() {
  const [nlEmail, setNlEmail] = useState('');
  const [nlStatus, setNlStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletter = async (e: FormEvent) => {
    e.preventDefault();
    if (!nlEmail) return;
    try {
      const response = await fetch('https://readdy.ai/api/form/d6alvstbd17lru29lti0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: nlEmail }).toString(),
      });
      if (response.ok) {
        setNlStatus('success');
        setNlEmail('');
      } else {
        setNlStatus('error');
      }
    } catch {
      setNlStatus('error');
    }
    setTimeout(() => setNlStatus('idle'), 4000);
  };

  return (
    <footer className="bg-stone-100 pt-16 pb-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img
                src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
                alt="House of FMR"
                className="h-12 w-12 object-contain"
              />
              <div>
                <p className="text-stone-900 font-display text-lg">House of FMR</p>
                <p className="text-amber-600 text-xs tracking-wider uppercase">Events &amp; Styling</p>
              </div>
            </Link>
            <p className="text-stone-500 text-sm leading-relaxed mb-5">
              Premium portable bar hire and event equipment across Melbourne and surrounds.
            </p>
            <form id="hire-newsletter-form" data-readdy-form onSubmit={handleNewsletter} className="flex gap-2">
              <input
                type="email"
                name="email"
                value={nlEmail}
                onChange={(e) => setNlEmail(e.target.value)}
                placeholder="Your email"
                required
                className="flex-1 px-4 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
              />
              <button
                type="submit"
                className="w-10 h-10 rounded-lg bg-amber-600 text-white flex items-center justify-center hover:bg-amber-500 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-send-plane-line text-sm"></i>
              </button>
            </form>
            {nlStatus === 'success' && (
              <p className="text-amber-600 text-xs mt-2">Subscribed successfully!</p>
            )}
            {nlStatus === 'error' && (
              <p className="text-rose-500 text-xs mt-2">Something went wrong. Try again.</p>
            )}
          </div>

          <div>
            <h4 className="text-amber-700 font-semibold text-xs tracking-wider uppercase mb-4">
              <a href="#portable-bars">Equipment</a>
            </h4>
            <ul className="space-y-2">
              {[
                { label: 'Portable Bars', target: 'portable-bars' },
                { label: 'Bar Mats & Accessories', target: 'bar-mats' },
                { label: 'Check Availability', target: 'booking-calendar' },
                { label: 'Get a Quote', target: 'hire-inquiry' },
              ].map(({ label, target }) => (
                <li key={label}>
                  <button
                    onClick={() => scrollToSection(target)}
                    className="text-stone-500 hover:text-amber-600 transition-colors cursor-pointer text-sm"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-amber-700 font-semibold text-xs tracking-wider uppercase mb-4">
              <a href="/">Quick Links</a>
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-stone-500 hover:text-amber-600 transition-colors cursor-pointer text-sm">Home</Link>
              </li>
              <li>
                <Link to="/shop" className="text-stone-500 hover:text-amber-600 transition-colors cursor-pointer text-sm">Shop</Link>
              </li>
              <li>
                <Link to="/services" className="text-stone-500 hover:text-amber-600 transition-colors cursor-pointer text-sm">Services</Link>
              </li>
              <li>
                <Link to="/gallery" className="text-stone-500 hover:text-amber-600 transition-colors cursor-pointer text-sm">Gallery</Link>
              </li>
              <li>
                <Link to="/invoice" className="text-stone-500 hover:text-amber-600 transition-colors cursor-pointer text-sm">Invoice Generator</Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-amber-700 font-semibold text-xs tracking-wider uppercase mb-4">
              <a href="#hire-inquiry">Contact</a>
            </h4>
            <ul className="space-y-2 text-sm text-stone-500">
              <li>
                <a href="mailto:hello@houseoffmr.com" className="hover:text-amber-600 transition-colors">hello@houseoffmr.com</a>
              </li>
              <li>
                <a href="tel:0411164144" className="hover:text-amber-600 transition-colors">0411 164 144</a>
              </li>
              <li>Melbourne, Victoria, Australia</li>
            </ul>
            <div className="flex gap-3 mt-4">
              {['instagram', 'facebook', 'pinterest'].map((social) => (
                <a
                  key={social}
                  href="#"
                  rel="nofollow"
                  className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center text-stone-500 hover:bg-amber-600 hover:text-white transition-all cursor-pointer"
                >
                  <i className={`ri-${social}-line`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-stone-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-400 text-sm">&copy; 2025 House of FMR Events &amp; Hire. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm">
            <a href="#" className="text-stone-400 hover:text-amber-600 transition-colors cursor-pointer">Privacy</a>
            <a href="#" className="text-stone-400 hover:text-amber-600 transition-colors cursor-pointer">Terms</a>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-amber-600 transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
